'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// ============================================
// Variants
// ============================================

export const kbdVariants = cva(
  'pointer-events-none inline-flex items-center justify-center select-none font-sans font-medium',
  {
    variants: {
      variant: {
        default:
          'bg-muted text-muted-foreground border border-border rounded-sm shadow-[var(--shadow-kbd-default)]',
        ghost: 'bg-transparent text-muted-foreground',
        outline:
          'bg-transparent border border-border text-muted-foreground rounded-sm shadow-[var(--shadow-kbd-outline)]',
        solid:
          'bg-card/60 border border-border text-muted-foreground rounded shadow-[var(--shadow-kbd-default)]',
        tooltip:
          'bg-background/20 text-background dark:bg-background/10 rounded-sm',
        footer:
          'bg-background border border-border text-secondary-foreground rounded-md shadow-[var(--shadow-kbd-footer)]',
      },
      size: {
        xs: 'h-4 min-w-4 px-0.5 text-[10px]',
        sm: 'h-5 min-w-5 px-1 text-xs',
        md: 'h-6 min-w-6 px-1.5 text-sm',
        lg: 'h-7 min-w-7 px-2 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
);

export const kbdGroupVariants = cva('inline-flex items-center', {
  variants: {
    gap: {
      none: 'gap-0',
      xs: 'gap-0.5',
      sm: 'gap-1',
      md: 'gap-1.5',
      lg: 'gap-2',
    },
  },
  defaultVariants: {
    gap: 'sm',
  },
});

// ============================================
// Types
// ============================================

export interface KbdProps
  extends React.ComponentProps<'kbd'>,
    VariantProps<typeof kbdVariants> {
  /** Icon to display before the key */
  icon?: React.ReactNode;
  /** Icon to display after the key */
  iconAfter?: React.ReactNode;
}

export interface KbdGroupProps
  extends React.ComponentProps<'span'>,
    VariantProps<typeof kbdGroupVariants> {}

// ============================================
// Kbd Component
// ============================================

/**
 * Keyboard key indicator component for displaying keyboard shortcuts.
 *
 * @example
 * // Basic usage
 * <Kbd>⌘K</Kbd>
 *
 * @example
 * // With variant and size
 * <Kbd variant="solid" size="md">⌘</Kbd>
 *
 * @example
 * // With icon
 * <Kbd icon={<ArrowUp />} />
 */
const Kbd = React.forwardRef<HTMLElement, KbdProps>(
  ({ className, variant, size, icon, iconAfter, children, ...props }, ref) => {
    return (
      <kbd
        ref={ref}
        data-slot="kbd"
        className={cn(kbdVariants({ variant, size }), className)}
        {...props}
      >
        {icon && (
          <span
            aria-hidden="true"
            className="shrink-0 [&_svg:not([class*='size-'])]:size-3"
          >
            {icon}
          </span>
        )}
        {children}
        {iconAfter && (
          <span
            aria-hidden="true"
            className="shrink-0 [&_svg:not([class*='size-'])]:size-3"
          >
            {iconAfter}
          </span>
        )}
      </kbd>
    );
  },
);
Kbd.displayName = 'Kbd';

// ============================================
// KbdGroup Component
// ============================================

/**
 * Group multiple Kbd components together with consistent spacing.
 *
 * @example
 * <KbdGroup>
 *   <Kbd>⌘</Kbd>
 *   <Kbd>K</Kbd>
 * </KbdGroup>
 */
const KbdGroup = React.forwardRef<HTMLSpanElement, KbdGroupProps>(
  ({ className, gap, ...props }, ref) => {
    return (
      <span
        ref={ref}
        data-slot="kbd-group"
        className={cn(kbdGroupVariants({ gap }), className)}
        {...props}
      />
    );
  },
);
KbdGroup.displayName = 'KbdGroup';

// ============================================
// Shortcut Component (convenience wrapper)
// ============================================

export interface ShortcutProps extends Omit<KbdProps, 'children'> {
  /** Array of keys to display (e.g., ['⌘', 'K']) */
  keys: (string | React.ReactNode)[];
  /** Separator between keys (defaults to none, keys are grouped) */
  separator?: React.ReactNode;
}

function getShortcutPartKey(
  key: string | React.ReactNode,
  position: number,
): string {
  return `${position}:${typeof key === 'string' ? key : 'node'}`;
}

/**
 * Convenience component for displaying keyboard shortcuts with multiple keys.
 *
 * @example
 * // Simple shortcut
 * <Shortcut keys={['⌘', 'K']} />
 *
 * @example
 * // With separator
 * <Shortcut keys={['⌘', 'Shift', 'P']} separator="+" />
 */
const Shortcut = React.forwardRef<HTMLSpanElement, ShortcutProps>(
  ({ keys, separator, className, variant, size, ...props }, ref) => {
    if (separator) {
      return (
        <KbdGroup ref={ref} gap="xs" className={className}>
          {keys.map((key, position) => (
            <React.Fragment key={getShortcutPartKey(key, position)}>
              <Kbd variant={variant} size={size} {...props}>
                {key}
              </Kbd>
              {position < keys.length - 1 && (
                <span className="text-muted-foreground text-xs">
                  {separator}
                </span>
              )}
            </React.Fragment>
          ))}
        </KbdGroup>
      );
    }

    return (
      <KbdGroup ref={ref} gap="xs" className={className}>
        {keys.map((key, position) => (
          <Kbd
            key={getShortcutPartKey(key, position)}
            variant={variant}
            size={size}
            {...props}
          >
            {key}
          </Kbd>
        ))}
      </KbdGroup>
    );
  },
);
Shortcut.displayName = 'Shortcut';

// ============================================
// Platform-aware helpers
// ============================================

/**
 * Common keyboard key symbols for macOS
 */
export const MAC_KEYS = {
  cmd: '⌘',
  command: '⌘',
  ctrl: '⌃',
  control: '⌃',
  alt: '⌥',
  option: '⌥',
  shift: '⇧',
  enter: '↵',
  return: '↵',
  backspace: '⌫',
  delete: '⌦',
  tab: '⇥',
  esc: 'esc',
  escape: 'esc',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: '␣',
} as const;

/**
 * Common keyboard key symbols for Windows/Linux
 */
export const WIN_KEYS = {
  cmd: 'Ctrl',
  command: 'Ctrl',
  ctrl: 'Ctrl',
  control: 'Ctrl',
  alt: 'Alt',
  option: 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  return: 'Enter',
  backspace: 'Backspace',
  delete: 'Delete',
  tab: 'Tab',
  esc: 'Esc',
  escape: 'Esc',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: 'Space',
} as const;

// ============================================
// Exports
// ============================================

export { Kbd, KbdGroup, Shortcut };
